import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Check, User, Building2, HardHat, Zap, Flame, RefreshCw, Film, Star } from 'lucide-react';
import { CheckoutDialog } from '@/components/advertise/CheckoutDialog';

interface PackageRow {
  id: string;
  name: string;
  audience: string;
  price_etb: number;
  duration_days: number;
  features: string[] | any;
  is_popular: boolean;
  is_active: boolean;
  sort_order: number;
}

interface PackageCardProps {
  pkg: PackageRow;
  onSelect: (pkg: PackageRow) => void;
  icon?: React.ComponentType<{ className?: string }>;
}

function PackageCard({ pkg, onSelect, icon: Icon }: PackageCardProps) {
  const features = toFeatureList(pkg.features);
  return (
    <Card
      className={`relative flex flex-col h-full transition-shadow hover:shadow-lg ${
        pkg.is_popular ? 'border-2 border-primary shadow-md' : ''
      }`}
    >
      {pkg.is_popular && (
        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-4 py-1">
          Most Popular
        </Badge>
      )}
      <CardHeader className="text-center pb-2 pt-6">
        {Icon && (
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Icon className="h-6 w-6 text-primary" />
          </div>
        )}
        <CardTitle className="text-lg">{pkg.name}</CardTitle>
        <div className="mt-2">
          <span className="text-3xl font-bold text-foreground">ETB {Number(pkg.price_etb).toLocaleString()}</span>
          <span className="text-sm text-muted-foreground">{periodLabel(pkg.duration_days)}</span>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col flex-1">
        <ul className="space-y-2.5 flex-1 mb-6">
          {features.map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
              <Check className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        <Button className="w-full" onClick={() => onSelect(pkg)}>Get Started</Button>
      </CardContent>
    </Card>
  );
}

const ICONS_BY_NAME: Record<string, any> = {
  'Listing Slot': Zap,
  'Hot Listing': Flame,
  'Super Hot Listing': Star,
  'Refresh Credits': RefreshCw,
  'Story Ads Credits': Film,
};

function periodLabel(days: number) {
  if (days === 30) return '/month';
  if (days === 365) return '/year';
  return `/${days} days`;
}

function toFeatureList(features: any): string[] {
  if (Array.isArray(features)) return features.filter((f) => typeof f === 'string');
  return [];
}

export default function Advertise() {
  const [checkoutPkg, setCheckoutPkg] = useState<PackageRow | null>(null);

  const { data: packages, isLoading } = useQuery({
    queryKey: ['advertising-packages-public'],
    queryFn: async () => {
      const { data } = await supabase
        .from('advertising_packages' as any)
        .select('*')
        .eq('is_active', true)
        .order('sort_order');
      return (data as unknown as PackageRow[]) || [];
    },
    staleTime: 60_000,
  });

  const grouped = useMemo(() => {
    const out: Record<string, PackageRow[]> = { individual: [], agency: [], developer: [] };
    (packages || []).forEach((p) => {
      const aud = (p.audience || 'individual').toLowerCase();
      if (out[aud]) out[aud].push(p);
    });
    return out;
  }, [packages]);

  const renderGrid = (rows: PackageRow[], cols: string) => {
    if (isLoading) {
      return (
        <div className={`grid ${cols} gap-6`}>
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-80 w-full" />)}
        </div>
      );
    }
    if (rows.length === 0) {
      return <p className="text-center text-muted-foreground py-12">No packages available right now. Check back soon.</p>;
    }
    return (
      <div className={`grid ${cols} gap-6`}>
        {rows.map((p) => (
          <PackageCard
            key={p.id}
            pkg={p}
            onSelect={setCheckoutPkg}
            icon={ICONS_BY_NAME[p.name]}
          />
        ))}
      </div>
    );
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-accent/5 to-secondary/5 py-16 md:py-24">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-primary/20 blur-3xl" />
          <div className="absolute bottom-10 right-20 w-48 h-48 rounded-full bg-accent/20 blur-3xl" />
        </div>
        <div className="container relative text-center">
          <h1 className="text-3xl md:text-5xl font-bold text-foreground mb-4">
            Let EthioRealEstates Build Your Business
          </h1>
          <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto">
            Reach millions of property seekers across Ethiopia. Choose a plan that fits your goals and
            start generating quality leads today.
          </p>
        </div>
      </section>

      {/* Tabs & Pricing */}
      <section className="container py-12 md:py-16">
        <Tabs defaultValue="individual" className="w-full">
          <div className="flex justify-center mb-10">
            <TabsList className="grid grid-cols-3 w-full max-w-md h-12">
              <TabsTrigger value="individual" className="gap-1.5 text-xs sm:text-sm">
                <User className="h-4 w-4" />
                <span className="hidden sm:inline">For</span> Individual
              </TabsTrigger>
              <TabsTrigger value="agency" className="gap-1.5 text-xs sm:text-sm">
                <Building2 className="h-4 w-4" />
                <span className="hidden sm:inline">For</span> Agency
              </TabsTrigger>
              <TabsTrigger value="developer" className="gap-1.5 text-xs sm:text-sm">
                <HardHat className="h-4 w-4" />
                <span className="hidden sm:inline">For</span> Developer
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="individual">
            {renderGrid(grouped.individual, 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5')}
          </TabsContent>
          <TabsContent value="agency">
            {renderGrid(grouped.agency, 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4')}
          </TabsContent>
          <TabsContent value="developer">
            {renderGrid(grouped.developer, 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4')}
          </TabsContent>
        </Tabs>
      </section>

      <CheckoutDialog
        open={!!checkoutPkg}
        onOpenChange={(o) => !o && setCheckoutPkg(null)}
        pkg={checkoutPkg}
      />
    </Layout>
  );
}
